const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
const TURNSTILE_READY_TIMEOUT_MS = 15_000

let loadPromise: Promise<void> | null = null

function waitForTurnstileGlobal(timeoutMs = TURNSTILE_READY_TIMEOUT_MS): Promise<void> {
  if (!import.meta.client) return Promise.resolve()
  if (window.turnstile) return Promise.resolve()

  return new Promise((resolve, reject) => {
    const startedAt = Date.now()

    const check = () => {
      if (window.turnstile) {
        resolve()
        return
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Turnstile timed out'))
        return
      }

      requestAnimationFrame(check)
    }

    check()
  })
}

function loadTurnstileScript() {
  if (!import.meta.client) return Promise.resolve()
  if (window.turnstile) return Promise.resolve()

  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const finish = () => {
      waitForTurnstileGlobal()
        .then(resolve)
        .catch((error) => {
          loadPromise = null
          reject(error)
        })
    }

    const fail = () => {
      loadPromise = null
      reject(new Error('Turnstile failed to load'))
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`)
    if (existing) {
      if (window.turnstile) {
        resolve()
        return
      }

      let settled = false
      const done = () => {
        if (settled) return
        settled = true
        finish()
      }

      existing.addEventListener('load', done, { once: true })
      existing.addEventListener('error', fail, { once: true })
      done()
      return
    }

    const script = document.createElement('script')
    script.src = TURNSTILE_SRC
    script.async = true
    script.onload = () => {
      script.dataset.turnstileLoaded = 'true'
      finish()
    }
    script.onerror = fail
    document.head.appendChild(script)
  })

  return loadPromise
}

export function loadTurnstileWithConsent() {
  if (!import.meta.client) return
  void loadTurnstileScript().catch(() => {})
}

export function useTurnstile() {
  const isReady = useState('turnstile-ready', () => false)
  const widgetId = useState<string | null>('turnstile-widget-id', () => null)

  function unmountTurnstile() {
    if (widgetId.value && window.turnstile) {
      try {
        window.turnstile.remove(widgetId.value)
      } catch {}
      widgetId.value = null
    }
    isReady.value = false
  }

  function teardownTurnstile() {
    unmountTurnstile()

    if (!import.meta.client) return

    loadPromise = null
    document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`)?.remove()
  }

  async function mountTurnstile(container: HTMLElement, siteKey: string) {
    if (!siteKey) return false

    await loadTurnstileScript()
    if (!window.turnstile) return false

    unmountTurnstile()

    widgetId.value = window.turnstile.render(container, {
      sitekey: siteKey,
      theme: 'light'
    })

    isReady.value = true
    return true
  }

  function resetTurnstile() {
    if (!widgetId.value || !window.turnstile) return
    window.turnstile.reset(widgetId.value)
  }

  return { isReady, mountTurnstile, unmountTurnstile, teardownTurnstile, resetTurnstile }
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: { sitekey: string; theme?: string }) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}